import React, { useState, useRef, useEffect } from 'react';
import { css } from '@emotion/react';
import { AiProfileInfo } from '@/customTypes/chapter';
import { AiCharacterImageType, MainCharacter } from '@/customTypes/storygame';
import { StoryGameCropIcon } from 'public/assets';

interface CharacterImageProps {
  character: AiProfileInfo;
  isActive: boolean;
  isDefault?: boolean;
  onProfileEdit: () => void;
}

const CharacterImage = ({
  character,
  isActive,
  isDefault,
  onProfileEdit,
}: CharacterImageProps) => {
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (character.profileFile) {
      const url = URL.createObjectURL(character.profileFile);
      setProfileImageUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setProfileImageUrl(null);
    }
  }, [character.profileFile]);

  const imageSrc = profileImageUrl || character.defaultProfileImageFile?.link;

  if (!imageSrc) {
    return (
      <div css={profileImageBorderStyle(isActive)}>
        <div css={profileImageStyle(isActive)} />
        {!isDefault && (
          <div css={profileEditIcn} onClick={onProfileEdit}>
            <StoryGameCropIcon />
          </div>
        )}
      </div>
    );
  }

  return (
    <div css={profileImageBorderStyle(isActive)}>
      <img
        src={imageSrc}
        alt={character.firstName}
        css={profileImageStyle(isActive)}
      />
      {!isDefault && (
        <div css={profileEditIcn} onClick={onProfileEdit}>
          <StoryGameCropIcon />
        </div>
      )}
    </div>
  );
};

const MobileStoryGameCharacterList = ({
  characterList,
  onClickStoryGameCharacter,
  selectedCharacter,
  aiCharacterImageList,
  onProfileEdit,
}: {
  characterList?: AiProfileInfo[];
  onClickStoryGameCharacter: (character: AiProfileInfo) => void;
  selectedCharacter?: MainCharacter;
  aiCharacterImageList?: AiCharacterImageType[];
  onProfileEdit: () => void;
}) => {
  const handleCharacterClick = (character: AiProfileInfo) => {
    onClickStoryGameCharacter(character);
    const element = document.getElementById(`profile-${character.characterId}`);
    element?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  };

  const scrollRef = useRef<HTMLDivElement>(null);

  return (
    <div css={scrollContainerStyle}>
      <div css={scrollContentStyle} ref={scrollRef}>
        {characterList?.map((character) => {
          const isDefault =
            aiCharacterImageList?.find((characterImage) => {
              return (
                characterImage.profileImage?.fileId ===
                character?.defaultProfileImageFile?.fileId
              );
            })?.isDefault ?? true;

          return (
            <div
              key={character.characterId}
              id={`profile-${character.characterId}`}
              css={profileItemStyle(
                selectedCharacter?.id === character.characterId,
              )}
              onClick={() => handleCharacterClick(character)}
            >
              <CharacterImage
                character={character}
                isActive={selectedCharacter?.id === character.characterId}
                onProfileEdit={onProfileEdit}
                isDefault={isDefault}
              />
              <span
                css={profileNameStyle(
                  selectedCharacter?.id === character.characterId,
                )}
              >
                {character.displayName}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const scrollContainerStyle = css`
  width: 100%;
  overflow-x: auto;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
  padding-top: 8px;
`;

const scrollContentStyle = css`
  display: flex;
  padding: 0px 16px;
  white-space: nowrap;
`;

const profileItemStyle = (isActive: boolean) => css`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 10px;
  cursor: pointer;
  position: relative;
  transition: all 0.3s ease;
  padding: 0px 0px 8px;

  &::after {
    content: '';
    position: absolute;
    bottom: 0px;
    left: 0;
    width: 100%;
    height: 2px;
    background-color: ${isActive ? 'white' : 'transparent'};
    transition: background-color 0.3s ease;
  }
`;

const profileImageStyle = (isActive: boolean) => css`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: contain;
  border: ${isActive ? '2px solid rgba(0,0,0,0.75)' : '2px solid transparent'};
  box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.25);
  transition: all 0.3s ease;
  background-color: #fff;
`;
const profileImageBorderStyle = (isActive: boolean) => css`
  border-radius: 50%;
  border: ${isActive ? '2px solid white' : '2px solid transparent'};
  transition: all 0.3s ease;
  background-color: transparent;

  justify-content: center;
  align-items: center;
  position: relative;
  display: flex;
`;

const profileNameStyle = (isActive: boolean) => css`
  margin-top: 5px;
  color: ${isActive ? '#ffffffe5' : '#FFFFFF80'};
  font-size: 12px;
  font-weight: ${isActive ? 'bold' : 'normal'};
  transition: all 0.3s ease;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 48px;
  overflow: hidden;
`;

const profileEditIcn = css`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  bottom: 0px;
  right: 0px;
  border-radius: 100px;
  cursor: pointer;
  backdrop-filter: blur(2px);
`;

export default MobileStoryGameCharacterList;
